/* eslint-disable @typescript-eslint/naming-convention */
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import uid from "common/utils/uid";
import type { DocEntityType } from "../doc/docEntityTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";

/**
 *  Entity types that can be on the source side of a DocEntityAssociation.
 */
export type DocAssociationSourceEntityType = Extract<DocEntityType, "FACADE" | "DATUM">;

/**
 *  Entity types that can be on the target side of a DocEntityAssociation.
 */
export type DocAssociationTargetEntityType = Extract<DocEntityType, "FACADE_SPAN_STRATUM" | "FACADE_SWEEP_STRATUM">;

export const docAssociationSourceEntityTypes: {
  [type in DocAssociationSourceEntityType]: type;
} = {
  FACADE: "FACADE",
  DATUM: "DATUM",
};

export const docAssociationTargetEntityTypes: {
  [type in DocAssociationTargetEntityType]: type;
} = {
  FACADE_SPAN_STRATUM: "FACADE_SPAN_STRATUM",
  FACADE_SWEEP_STRATUM: "FACADE_SWEEP_STRATUM",
};

/**
 * Valid entity type pairs for associations.
 */
export const docAssociationSourceTypeToTargetType = {
  FACADE: ["FACADE_SPAN_STRATUM", "FACADE_SWEEP_STRATUM"],
  DATUM: ["FACADE_SPAN_STRATUM"],
} as const satisfies Record<DocAssociationSourceEntityType, DocAssociationTargetEntityType[]>;

export const docAssociationTargetTypeToSourceType = {
  FACADE_SPAN_STRATUM: ["FACADE", "DATUM"],
  FACADE_SWEEP_STRATUM: ["FACADE"],
} as const satisfies Record<DocAssociationTargetEntityType, DocAssociationSourceEntityType[]>;

export type DocAssociationSourceTypeToTargetType = {
  [K in DocAssociationSourceEntityType]: (typeof docAssociationSourceTypeToTargetType)[K][number];
};

export type DocAssociationTargetTypeToSourceType = {
  [K in DocAssociationTargetEntityType]: (typeof docAssociationTargetTypeToSourceType)[K][number];
};

/**
 * A nominal type representing a string reference to a DocEntityAssociation.
 */
export type DocEntityAssociationId<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
> = string & {
  readonly __sourceType: S;
  readonly __targetType: T;
};

/**
 * Base type for all DocEntityAssociations.
 */
export interface DocEntityAssociationBase<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
> extends FormulaDisabled {
  // The unique id of the association.
  readonly id: DocEntityAssociationId<S, T>;
  // The source entity of the association.
  readonly sourceEntityId: ForeignEntityId<S>;
  // The target entity of the association.
  readonly targetEntityId: ForeignEntityId<T>;
  // The sort index of the association.
  readonly sortIndex: number;
}

/**
 * A DocEntityAssociation that has a disambiguator. This is needed when the same source and target entity types are used
 * in different contexts.
 */
export interface DocEntityAssociationWithDisambiguator<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
  Disambiguator extends string,
> extends DocEntityAssociationBase<S, T> {
  readonly disambiguator: Disambiguator;
}

/**
 * Specific DocEntityAssociation types.
 */
export interface DocFacadeSpanStratumAssociation extends DocEntityAssociationBase<"FACADE", "FACADE_SPAN_STRATUM"> {}

export interface DocFacadeSweepStratumAssociation extends DocEntityAssociationBase<"FACADE", "FACADE_SWEEP_STRATUM"> {}

export interface DocDatumSpanAssociation
  extends DocEntityAssociationWithDisambiguator<"DATUM", "FACADE_SPAN_STRATUM", "UPPER" | "LOWER" | "START" | "END"> {}

export type DocEntityAssociation =
  | DocFacadeSpanStratumAssociation
  | DocFacadeSweepStratumAssociation
  | DocDatumSpanAssociation;

/**
 * Creates a new DocEntityAssociationId using `uid`. This is a nominal type, so the value is just a string.
 */
export function docEntityAssociationIdNew<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
>(_sourceType: S, _targetType: T): DocEntityAssociationId<S, T> {
  return uid() as DocEntityAssociationId<S, T>;
}

/**
 * Creates a new DocEntityAssociationId using a string argument. This is a nominal type, so the value is just a string.
 */
export function docEntityAssociationIdFromString<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
>(_sourceType: S, _targetType: T, s: string): DocEntityAssociationId<S, T> {
  return s as DocEntityAssociationId<S, T>;
}
